import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../lib/api';
import type { AppNotification, NotificationKind } from '../lib/types';

// Polling cadence (ms). Pauses cuando document.hidden = true.
const POLL_INTERVAL_MS = 60_000;
const DROPDOWN_LIMIT = 10;

// ── Notificación de BIENVENIDA · cliente (contenido estático honesto) ──────
// Siempre presente para cualquier alumno (Start/Basic/Pro/Elite). No viene del
// backend: es un mensaje fijo de onboarding. Se marca como leída de forma local
// (localStorage) para que cuente en la campanita la primera vez y luego no
// vuelva a insistir. Id sentinela para distinguirla de las del backend.
const WELCOME_ID = '__welcome__';
const WELCOME_READ_KEY = 'robotea-welcome-read';

function isWelcomeRead(): boolean {
  try {
    return localStorage.getItem(WELCOME_READ_KEY) === '1';
  } catch {
    return false;
  }
}

function markWelcomeReadStorage(): void {
  try {
    localStorage.setItem(WELCOME_READ_KEY, '1');
  } catch {
    /* storage no disponible · solo runtime */
  }
}

function buildWelcomeNotification(read: boolean): AppNotification {
  return {
    id: WELCOME_ID,
    user_id: WELCOME_ID,
    kind: 'system',
    title: '¡Bienvenido a Robotea! 👋',
    body: 'Aquí empieza tu camino con los robots. Abre tu primer curso, configura tu robot y pásate por la comunidad para conocer al resto. Estamos contigo en cada paso.',
    link_url: '/dashboard',
    metadata: { welcome: true },
    read_at: read ? new Date(0).toISOString() : null,
    created_at: null,
  };
}

// Mapeo kind → icono renderizable. Sin emojis "decorativos" sueltos: forman
// parte del UX informativo del kind (mapping semantico).
const KIND_ICON: Record<NotificationKind, string> = {
  license_approved: '🤖',
  license_rejected: '🤖',
  comment_reply: '💬',
  comment_mention: '💬',
  achievement_earned: '🏆',
  course_announcement: '📚',
  admin_message: '📢',
  system: 'ℹ️',
};

function getRelativeTime(iso: string | null): string {
  if (!iso) return '';
  const now = Date.now();
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diffSec = Math.max(0, Math.floor((now - t) / 1000));
  if (diffSec < 60) return 'ahora mismo';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `hace ${diffMin} min`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `hace ${diffHr} h`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `hace ${diffDay} d`;
  return new Date(iso).toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
  });
}

export default function NotificationBell(): JSX.Element | null {
  const { state } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bumped, setBumped] = useState(false);
  // Estado leído/no-leído de la notificación de bienvenida (persistido local).
  const [welcomeRead, setWelcomeRead] = useState<boolean>(() => isWelcomeRead());
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const prevUnreadRef = useRef<number>(0);

  const auth = state.status === 'authenticated' ? state.identity : null;

  const load = useCallback(
    async (silent = false): Promise<void> => {
      if (!auth) return;
      if (!silent) setLoading(true);
      try {
        const data = await fetchNotifications(false, DROPDOWN_LIMIT, auth);
        setItems(data.notifications);
        setUnreadCount(data.unread_count);
        setError(null);
        // Animación bell si llegan nuevas no leídas
        if (data.unread_count > prevUnreadRef.current) {
          setBumped(true);
          setTimeout(() => setBumped(false), 1200);
        }
        prevUnreadRef.current = data.unread_count;
      } catch {
        // Degradación graceful · no romper UI si API falla
        setError('No se pudieron cargar las notificaciones');
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [auth],
  );

  // Carga inicial + polling con Page Visibility API
  useEffect(() => {
    if (!auth) return;
    void load(true);

    let interval: ReturnType<typeof setInterval> | null = null;

    function startPolling(): void {
      if (interval !== null) return;
      interval = setInterval(() => {
        if (typeof document === 'undefined' || !document.hidden) {
          void load(true);
        }
      }, POLL_INTERVAL_MS);
    }

    function stopPolling(): void {
      if (interval !== null) {
        clearInterval(interval);
        interval = null;
      }
    }

    function onVisibility(): void {
      if (typeof document === 'undefined') return;
      if (document.hidden) {
        stopPolling();
      } else {
        // Refetch immediate al volver al tab + reanudar polling
        void load(true);
        startPolling();
      }
    }

    startPolling();
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }
    return () => {
      stopPolling();
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    };
  }, [auth, load]);

  // Click-outside + Esc para cerrar
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent): void {
      const target = e.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleToggle = useCallback((): void => {
    setOpen((v) => {
      const next = !v;
      if (next) void load();
      return next;
    });
  }, [load]);

  const handleItemClick = useCallback(
    async (notif: AppNotification): Promise<void> => {
      if (!auth) return;
      // Notificación de bienvenida · es cliente. Marca leída en localStorage
      // (no hay endpoint que llamar) y navega a su CTA.
      if (notif.id === WELCOME_ID) {
        markWelcomeReadStorage();
        setWelcomeRead(true);
        setOpen(false);
        if (notif.link_url) navigate(notif.link_url);
        return;
      }
      // Optimistic UI
      if (notif.read_at === null) {
        setItems((curr) =>
          curr.map((n) =>
            n.id === notif.id ? { ...n, read_at: new Date().toISOString() } : n,
          ),
        );
        setUnreadCount((c) => Math.max(0, c - 1));
        try {
          await markNotificationRead(notif.id, auth);
        } catch {
          // Rollback silencioso · próximo poll lo corrige
        }
      }
      setOpen(false);
      if (notif.link_url) {
        navigate(notif.link_url);
      }
    },
    [auth, navigate],
  );

  const handleMarkAllRead = useCallback(async (): Promise<void> => {
    if (!auth) return;
    // La bienvenida también se da por leída (estado local).
    markWelcomeReadStorage();
    setWelcomeRead(true);
    const prevItems = items;
    const prevCount = unreadCount;
    setItems((curr) =>
      curr.map((n) =>
        n.read_at === null
          ? { ...n, read_at: new Date().toISOString() }
          : n,
      ),
    );
    setUnreadCount(0);
    try {
      await markAllNotificationsRead(auth);
    } catch {
      // Rollback
      setItems(prevItems);
      setUnreadCount(prevCount);
    }
  }, [auth, items, unreadCount]);

  if (state.status !== 'authenticated') {
    return null;
  }

  // La bienvenida se inyecta SIEMPRE al principio de la lista. Su no-leída
  // suma al contador hasta que el alumno la abre (o "marcar todas como leídas").
  const welcome = buildWelcomeNotification(welcomeRead);
  // `items` siempre debería ser un array (init []), pero nos protegemos por si
  // una respuesta inesperada del backend lo deja en otra forma: la bienvenida
  // debe poder mostrarse igualmente.
  const backendItems = Array.isArray(items) ? items : [];
  const displayItems: AppNotification[] = [welcome, ...backendItems];
  const totalUnread = unreadCount + (welcomeRead ? 0 : 1);
  const displayCount = totalUnread > 99 ? '99+' : String(totalUnread);

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggle}
        aria-label={`Notificaciones${totalUnread > 0 ? ` · ${totalUnread} sin leer` : ''}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        className={`relative inline-flex items-center justify-center rounded-md border border-ink/10 bg-ink/5 p-2 text-ink/65 transition hover:border-ink/20 hover:bg-ink/10 hover:text-ink ${
          bumped ? 'animate-[bell-pulse_1.2s_ease-in-out]' : ''
        }`}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
        {totalUnread > 0 && (
          <span
            className="absolute -right-1 -top-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-ink ring-2 ring-canvas"
            aria-hidden="true"
          >
            {displayCount}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={dropdownRef}
          role="dialog"
          aria-label="Bandeja de notificaciones"
          className="absolute right-0 z-40 mt-2 w-[22rem] max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-xl border border-ink/10 bg-surface shadow-2xl"
        >
          <header className="flex items-center justify-between gap-3 border-b border-ink/10 px-4 py-3">
            <h2 className="text-sm font-semibold text-ink/85">
              Notificaciones
            </h2>
            {totalUnread > 0 && (
              <button
                type="button"
                onClick={() => void handleMarkAllRead()}
                className="text-xs text-brand-violet hover:text-brand-violet/80"
              >
                Marcar todas como leídas
              </button>
            )}
          </header>

          <div className="max-h-[60vh] overflow-y-auto">
            {error && (
              <p className="border-b border-amber-400/30 bg-amber-500/10 px-4 py-2 text-center text-xs text-amber-700/90 dark:text-amber-300/80">
                {error}
              </p>
            )}
            <ul className="divide-y divide-ink/5">
              {displayItems.map((notif) => {
                const isWelcome = notif.id === WELCOME_ID;
                const unread = notif.read_at === null;
                return (
                  <li key={notif.id}>
                    <button
                      type="button"
                      onClick={() => void handleItemClick(notif)}
                      className={`flex w-full items-start gap-3 px-4 py-3 text-left transition hover:bg-ink/5 ${
                        isWelcome
                          ? 'bg-[color-mix(in_oklch,var(--sig-inicio)_8%,transparent)]'
                          : unread
                            ? 'bg-brand-violet/5'
                            : ''
                      }`}
                    >
                      <span
                        className={`mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-base ${
                          isWelcome
                            ? 'bg-[color-mix(in_oklch,var(--sig-inicio)_20%,transparent)]'
                            : 'bg-ink/5'
                        }`}
                        aria-hidden="true"
                      >
                        {isWelcome ? '🚀' : (KIND_ICON[notif.kind] ?? 'ℹ️')}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <p
                            className={`truncate text-sm ${
                              unread ? 'font-semibold text-ink/90' : 'text-ink/75'
                            }`}
                          >
                            {notif.title}
                          </p>
                          {unread && (
                            <span
                              aria-label="No leída"
                              className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand-violet"
                            />
                          )}
                        </div>
                        {notif.body && (
                          <p
                            className={`mt-0.5 text-xs text-ink/55 ${
                              isWelcome ? '' : 'line-clamp-2'
                            }`}
                          >
                            {notif.body}
                          </p>
                        )}
                        {isWelcome ? (
                          <p className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--sig-inicio)]">
                            Empezar ahora
                            <span aria-hidden="true">→</span>
                          </p>
                        ) : (
                          <p className="mt-1 text-[11px] uppercase tracking-wider text-ink/35">
                            {getRelativeTime(notif.created_at)}
                          </p>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
            {loading && (
              <p className="px-4 py-3 text-center text-xs text-ink/40">
                Cargando más…
              </p>
            )}
          </div>

          <footer className="border-t border-ink/10 px-4 py-2 text-center">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate('/notificaciones');
              }}
              className="text-xs text-ink/60 hover:text-ink"
            >
              Ver todas
            </button>
          </footer>
        </div>
      )}
    </div>
  );
}
